import { useState, useEffect } from 'react';

export interface CustomPositionMapping {
  symbol: string;
  tvToCtraderRatio: number; // 1 TV position = X cTrader lots
  lastUpdated: string;
  source: 'user' | 'default';
}

export const useCustomPositionMappings = () => {
  const [customMappings, setCustomMappings] = useState<Map<string, CustomPositionMapping>>(new Map());
  const [isLoaded, setIsLoaded] = useState(false);

  // Load mappings from localStorage
  useEffect(() => {
    const loadMappings = () => {
      try {
        const saved = localStorage.getItem('custom_position_mappings');
        if (saved) {
          const parsed = JSON.parse(saved);
          const mappings = new Map<string, CustomPositionMapping>();
          Object.entries(parsed).forEach(([symbol, mapping]) => {
            mappings.set(symbol, mapping as CustomPositionMapping);
          });
          setCustomMappings(mappings);
        }
      } catch (error) {
        console.error('Error loading custom position mappings:', error);
      } finally {
        setIsLoaded(true);
      }
    };

    loadMappings();
  }, []);

  // Save mappings to localStorage
  const saveMappings = (mappings: Map<string, CustomPositionMapping>) => {
    try {
      const obj = Object.fromEntries(mappings);
      localStorage.setItem('custom_position_mappings', JSON.stringify(obj));
      setCustomMappings(new Map(mappings));
    } catch (error) {
      console.error('Error saving custom position mappings:', error);
    }
  };

  // Add or update a mapping
  const addMapping = (symbol: string, ratio: number) => {
    const mapping: CustomPositionMapping = {
      symbol: symbol.toUpperCase(),
      tvToCtraderRatio: ratio,
      lastUpdated: new Date().toISOString(),
      source: 'user'
    };

    const updatedMappings = new Map(customMappings);
    updatedMappings.set(mapping.symbol, mapping);
    saveMappings(updatedMappings);
  };

  // Remove a mapping
  const removeMapping = (symbol: string) => {
    const updatedMappings = new Map(customMappings);
    updatedMappings.delete(symbol.toUpperCase());
    saveMappings(updatedMappings);
  };

  // Get mapping for a symbol
  const getMapping = (symbol: string): CustomPositionMapping | undefined => {
    return customMappings.get(symbol.toUpperCase());
  };

  // Check if symbol has custom mapping
  const hasMapping = (symbol: string): boolean => {
    return customMappings.has(symbol.toUpperCase());
  };

  return {
    customMappings,
    isLoaded,
    addMapping,
    removeMapping,
    getMapping,
    hasMapping,
    updateMappings: setCustomMappings
  };
};